/**
 * Commitment Posture Coach — public lead magnet
 * ================================================
 * Free, no-signup. Tells founders / FinOps practitioners exactly what
 * % SP/RI coverage to target based on workload stability — the question
 * every Cost & Usage Report dashboard shows but never answers.
 *
 * Style matches /ai-profitability + /savings-calculator + /bill-score
 * so the free-tools surface feels like one product.
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import SEOHead from '../components/SEOHead';
import FAQSection from '../components/FAQSection';
import { faqsByPage } from '../data/faqs';
import {
  ArrowLeft, Sparkles, Mail, CheckCircle2, Loader2,
  Calculator, ShieldCheck, Beaker, TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '$0';
  const a = Math.abs(n); const sign = n < 0 ? '-' : '';
  if (a >= 1_000_000) return `${sign}$${(a / 1_000_000).toFixed(1)}M`;
  if (a >= 1_000) return `${sign}$${(a / 1_000).toFixed(0)}K`;
  return `${sign}$${a.toFixed(0)}`;
};

const fmtMoneyExact = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '$0';
  return `$${Math.round(n).toLocaleString()}`;
};

const PROVIDERS = [
  { key: 'aws',   label: 'AWS (Compute Savings Plans)', term: '1y' },
  { key: 'azure', label: 'Azure (Reserved Instances)',  term: '1y' },
  { key: 'gcp',   label: 'GCP (Committed Use Discounts)', term: '1y' },
];

const VOLATILITY_PRESETS = [
  { key: 'rocksteady', cv: 2.0,  label: 'Rock-steady', hint: 'Predictable workload, <5% day-to-day variance.' },
  { key: 'stable',     cv: 10.0, label: 'Stable',      hint: 'Mild seasonality, weekday/weekend pattern.' },
  { key: 'mixed',      cv: 22.0, label: 'Mixed',       hint: 'Some seasonal spikes, growing fast.' },
  { key: 'volatile',   cv: 40.0, label: 'Volatile',    hint: 'Spiky / batch-heavy / heavy-AI inference.' },
];

const CommitmentCoach = () => {
  const [spend, setSpend] = useState(100_000);
  const [coverage, setCoverage] = useState(38.9);
  const [cv, setCv] = useState(10.0);
  const [provider, setProvider] = useState('aws');
  const [term, setTerm] = useState('1y');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const [emailOpen, setEmailOpen] = useState(false);
  const [emailDone, setEmailDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [eEmail, setEEmail] = useState('');
  const [eName, setEName] = useState('');
  const [eCompany, setECompany] = useState('');

  const compute = useMemo(
    () => async () => {
      if (!spend || spend <= 0) return;
      setLoading(true);
      try {
        const { data } = await axios.post(`${API_URL}/commitment-coach/analyze`, {
          monthly_compute_spend_usd: Number(spend),
          current_coverage_pct: Number(coverage),
          spend_cv_pct: Number(cv),
          provider,
          term,
        });
        setResult(data);
      } catch (e) {
        toast.error('Could not compute', { description: e?.response?.data?.detail || e?.message });
      } finally { setLoading(false); }
    }, [spend, coverage, cv, provider, term],
  );

  useEffect(() => {
    const t = setTimeout(() => { compute(); }, 350);
    return () => clearTimeout(t);
  }, [compute]);

  const submitEmail = async () => {
    if (!eEmail || !eEmail.includes('@')) {
      toast.error('Please enter a valid email'); return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API_URL}/commitment-coach/waitlist`, {
        email: eEmail.trim(),
        name: eName.trim() || undefined,
        company: eCompany.trim() || undefined,
        monthly_spend_usd: Number(spend),
        current_coverage_pct: Number(coverage),
        annual_savings_delta_usd: result?.savings_breakdown?.annual_savings_delta_usd || 0,
      });
      setEmailDone(true);
      toast.success(r.data?.message || 'Welcome — we\'ll be in touch shortly.');
    } catch (e) {
      toast.error('Could not save your details', { description: e?.response?.data?.detail || e?.message });
    } finally { setSubmitting(false); }
  };

  const verdictColor = result?.verdict?.tone === 'emerald'
    ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30'
    : 'bg-amber-500/10 text-amber-300 border-amber-500/30';

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100" data-testid="commitment-coach-page">
      <SEOHead
        title="Commitment Posture Coach — Free FinOps Tool"
        description="What % Savings Plans / RI / CUD coverage should you target? Free 90-second tool — show your monthly spend and workload stability, get the optimal coverage band with the math shown."
        canonical="https://cartieai.com/commitment-coach"
        keywords="AWS Savings Plans optimal coverage, RI coverage, CUD coverage, FinOps commitment, reserved instance optimization"
      />

      {/* Top nav (matches Savings Calculator + Bill Score + AI Profitability) */}
      <nav className="fixed top-0 w-full z-50 bg-slate-900 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src="/icons/cartie-emblem.png" alt="CARTIEAI" className="w-10 h-10 object-contain" />
              <span className="text-xl font-bold">
                <span className="text-white">CARTIE<span className="text-emerald-400">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
              </span>
            </Link>
            <Link to="/">
              <Button variant="outline" className="border-slate-700 text-slate-600 hover:bg-slate-800" data-testid="back-link">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto space-y-8">
          {/* Hero */}
          <header>
            <Badge className="mb-3 bg-emerald-500/10 text-emerald-300 border-emerald-500/30 tracking-wider">
              <Calculator className="w-3 h-3 mr-1" /> FREE TOOL · NO SIGN-UP
            </Badge>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 mb-3">
              What's your{' '}
              <span className="text-emerald-600">
                optimal commitment posture?
              </span>
            </h1>
            <p className="text-slate-600 max-w-3xl text-base sm:text-lg leading-relaxed">
              Every cost dashboard shows your <span className="text-slate-900 font-medium">current</span> Savings-Plan or Reserved-Instance coverage. Almost none answer the question that actually matters:{' '}
              <span className="text-slate-900 font-medium">"What coverage % should I target?"</span>
            </p>
            <p className="text-slate-400 text-sm mt-3">
              Fill three numbers below → see the optimal band, the annual $ delta, and exactly how much incremental SP/RI to buy. The math is shown — nothing hidden.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 text-sm">
              <span className="text-slate-400">Workload drifts? See the</span>
              <Link to="/commitment-rebalancer" className="text-emerald-400 hover:text-emerald-300 underline font-medium" data-testid="link-to-rebalancer">
                continuous rebalancer →
              </Link>
            </div>
          </header>

          {/* Inputs */}
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="bg-slate-900/60 border-white/10">
              <CardHeader>
                <CardTitle className="text-slate-900 text-base flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-emerald-400" /> Your numbers
                </CardTitle>
                <CardDescription>Three values from your last cost dashboard.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div>
                  <label className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1 block">
                    Monthly compute spend (USD)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-slate-400">$</span>
                    <Input type="number" min={0} step={1000} value={spend}
                      onChange={(e) => setSpend(Number(e.target.value))}
                      className="pl-7 bg-slate-950 border-slate-700 text-white text-xl font-semibold"
                      data-testid="input-spend" />
                  </div>
                  <p className="text-xs text-slate-400 mt-2 font-mono">≈ {fmtMoney(spend * 12)} / year</p>
                </div>

                <div>
                  <label className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1 block">
                    Current SP/RI/CUD coverage (%)
                  </label>
                  <div className="relative">
                    <Input type="number" min={0} max={100} step={0.1} value={coverage}
                      onChange={(e) => setCoverage(Number(e.target.value))}
                      className="bg-slate-950 border-slate-700 text-white text-xl font-semibold pr-8"
                      data-testid="input-coverage" />
                    <span className="absolute right-3 top-2.5 text-slate-400">%</span>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1.5">From your CUR / Cost Explorer / Azure Cost Mgmt.</p>
                </div>

                <div>
                  <label className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-2 block">
                    Workload stability
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {VOLATILITY_PRESETS.map((p) => (
                      <button key={p.key} type="button"
                        onClick={() => setCv(p.cv)}
                        data-testid={`preset-${p.key}`}
                        className={`text-left p-3 rounded-md border transition-all ${
                          Math.abs(cv - p.cv) < 1
                            ? 'border-emerald-500/40 bg-emerald-500/10 text-white'
                            : 'border-white/10 hover:border-emerald-500/30 hover:bg-white/5 text-slate-300'
                        }`}>
                        <p className="font-semibold text-sm">{p.label}</p>
                        <p className="text-[11px] text-slate-400 mt-0.5">{p.hint}</p>
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-2">
                    Or set the spend coefficient of variation directly:{' '}
                    <input type="number" min={0} max={200} step={0.5} value={cv}
                      onChange={(e) => setCv(Number(e.target.value))}
                      className="bg-slate-950 border border-slate-700 rounded px-1.5 py-0.5 w-16 text-emerald-300 font-mono text-xs"
                      data-testid="input-cv" />% CV
                  </p>
                </div>

                <div>
                  <label className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-2 block">
                    Cloud provider · term
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {PROVIDERS.map((p) => (
                      <button key={p.key} type="button" onClick={() => setProvider(p.key)}
                        data-testid={`provider-${p.key}`}
                        className={`p-2.5 rounded-md border text-xs font-semibold transition-all ${
                          provider === p.key
                            ? 'border-emerald-500/40 bg-emerald-500/10 text-white'
                            : 'border-white/10 hover:border-emerald-500/30 text-slate-300'
                        }`}>
                        {p.key.toUpperCase()}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-2">
                    {['1y', '3y'].map((t) => (
                      <button key={t} type="button" onClick={() => setTerm(t)}
                        data-testid={`term-${t}`}
                        className={`flex-1 p-2 rounded-md border text-xs font-semibold transition-all ${
                          term === t
                            ? 'border-emerald-500/40 bg-emerald-500/10 text-white'
                            : 'border-white/10 hover:border-emerald-500/30 text-slate-300'
                        }`}>
                        {t === '1y' ? '1-year term' : '3-year term'}
                      </button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Result */}
            <Card className="bg-gradient-to-br from-emerald-500/10 via-slate-900/60 to-slate-900/60 border-emerald-500/30" data-testid="result-card">
              <CardHeader>
                <CardTitle className="text-slate-900 text-base flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-emerald-400" /> Your optimal posture
                </CardTitle>
                <CardDescription>
                  {result?.explanation || 'Computing…'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                {!result ? (
                  <div className="py-12 text-center text-slate-400">
                    <Loader2 className="h-6 w-6 animate-spin inline mr-2" /> Computing…
                  </div>
                ) : (
                  <>
                    <Badge className={`${verdictColor} px-3 py-1.5 text-sm`} data-testid="verdict-badge">
                      {result.verdict.headline}
                    </Badge>

                    <div className="bg-slate-950/60 rounded-lg p-4 border border-white/5">
                      <p className="text-[11px] uppercase tracking-widest text-slate-400 mb-2">Recommended coverage band</p>
                      <p className="text-3xl sm:text-4xl font-extrabold text-slate-900 font-mono" data-testid="optimal-band">
                        {result.optimal_band.low}% – {result.optimal_band.high}%
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        Sweet spot: <span className="text-emerald-400 font-mono font-bold">{result.optimal_band.sweet_spot}%</span> · You're at <span className="text-slate-900 font-mono">{coverage}%</span>
                      </p>
                    </div>

                    <div>
                      <p className="text-[11px] uppercase tracking-widest text-slate-400 mb-2">Annual savings opportunity</p>
                      <p className={`text-3xl sm:text-4xl font-extrabold font-mono ${result.savings_breakdown.annual_savings_delta_usd > 0 ? 'text-emerald-400' : 'text-slate-300'}`} data-testid="savings-delta">
                        {result.savings_breakdown.annual_savings_delta_usd > 0 ? '+' : ''}{fmtMoneyExact(result.savings_breakdown.annual_savings_delta_usd)}<span className="text-base text-slate-400">/yr</span>
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        From {fmtMoney(result.savings_breakdown.annual_at_current_coverage_usd)} (today) → {fmtMoney(result.savings_breakdown.annual_at_sweet_spot_usd)} (at {result.optimal_band.sweet_spot}%)
                      </p>
                    </div>

                    {result.recommended_buy.incremental_annual_commitment_usd > 0 && (
                      <div className="bg-slate-950/40 rounded-lg p-4 border border-emerald-500/20">
                        <p className="text-[11px] uppercase tracking-widest text-emerald-400 mb-1">Recommended action</p>
                        <p className="text-sm text-slate-900" data-testid="recommended-buy-narrative">
                          {result.recommended_buy.narrative}
                        </p>
                      </div>
                    )}

                    <Button onClick={() => setEmailOpen(true)} size="lg"
                      className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
                      data-testid="request-plan-btn">
                      <Mail className="h-4 w-4 mr-2" /> Get a tailored buy plan
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Coverage scenarios */}
          {result && (
            <Card className="bg-slate-900/60 border-white/10">
              <CardHeader>
                <CardTitle className="text-slate-900 text-base">Annual savings at every coverage point</CardTitle>
                <CardDescription>
                  Discount used: <span className="text-emerald-300 font-mono">{result.discount_pct_used}%</span> ({provider.toUpperCase()} · {term} term).
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="scenarios-grid">
                  {[
                    { label: 'Today', pct: coverage, val: result.savings_breakdown.annual_at_current_coverage_usd, accent: 'slate' },
                    { label: `Low band (${result.optimal_band.low}%)`, pct: result.optimal_band.low, val: result.savings_breakdown.annual_at_low_band_usd, accent: 'emerald' },
                    { label: `Sweet spot (${result.optimal_band.sweet_spot}%)`, pct: result.optimal_band.sweet_spot, val: result.savings_breakdown.annual_at_sweet_spot_usd, accent: 'emerald' },
                    { label: `High band (${result.optimal_band.high}%)`, pct: result.optimal_band.high, val: result.savings_breakdown.annual_at_high_band_usd, accent: 'emerald' },
                  ].map((s, i) => (
                    <div key={i} className={`p-4 rounded-lg border ${
                      s.accent === 'emerald' ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-slate-950/40 border-white/10'
                    }`}>
                      <p className="text-[11px] uppercase tracking-widest text-slate-400 mb-1">{s.label}</p>
                      <p className={`text-xl font-bold font-mono ${s.accent === 'emerald' ? 'text-emerald-400' : 'text-slate-300'}`}>
                        {fmtMoney(s.val)}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Math */}
          <Card className="bg-slate-900/40 border-white/10">
            <CardHeader>
              <CardTitle className="text-slate-900 text-base flex items-center gap-2">
                <Beaker className="h-5 w-5 text-emerald-400" /> The math (transparent on purpose)
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2 text-slate-400">
              <p><span className="text-slate-900 font-semibold">Optimal band</span> is set by your spend volatility (CV %). Stable workloads can safely commit a high share; volatile workloads need flexibility:</p>
              <ul className="text-xs font-mono pl-4 space-y-0.5">
                <li>CV &lt; 5% → <span className="text-emerald-300">60–70%</span> (rock-steady, commit aggressively)</li>
                <li>CV 5–15% → <span className="text-emerald-300">45–55%</span> (stable + mild seasonality)</li>
                <li>CV 15–30% → <span className="text-emerald-300">30–40%</span> (mixed / growing fast)</li>
                <li>CV &gt; 30% → <span className="text-emerald-300">15–25%</span> (volatile / spiky / batch)</li>
              </ul>
              <p><span className="text-slate-900 font-semibold">Annual savings</span> = monthly spend × 12 × coverage × discount. Discounts are public industry numbers (Jan 2026 snapshot): AWS 1y SP ≈ 28%, AWS 3y ≈ 47%, Azure RI 1y ≈ 30%, Azure 3y ≈ 55%, GCP CUD 1y ≈ 25%, GCP 3y ≈ 55%.</p>
              <p className="text-xs italic pt-2 border-t border-white/5 text-slate-400">
                Why we built this: the question "what % SP/RI coverage should I target?" is the most under-served question in FinOps. Apptio and Cloudability answer it for $50K/year. We make the math free.
              </p>
              <div className="pt-3 flex gap-2 flex-wrap">
                <Link to="/savings-calculator"><Button size="sm" variant="outline" className="border-slate-700 text-slate-600">Savings Calculator →</Button></Link>
                <Link to="/ai-profitability"><Button size="sm" variant="outline" className="border-slate-700 text-slate-600">AI Feature Profitability →</Button></Link>
                <Link to="/customer-pl"><Button size="sm" variant="outline" className="border-slate-700 text-slate-600">Customer P&amp;L →</Button></Link>
              </div>
            </CardContent>
          </Card>

          {loading && (
            <div className="text-center text-xs text-slate-400" data-testid="loading-indicator">
              <Loader2 className="h-3 w-3 inline animate-spin mr-1" /> recomputing…
            </div>
          )}
        </div>
      </main>

      {/* Email capture dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="bg-slate-900 border-white/10 text-slate-100" data-testid="email-dialog">
          {!emailDone ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-white">Get your tailored commitment buy plan</DialogTitle>
                <DialogDescription className="text-slate-400">
                  Personalised for your stack, written by a human, sent within 24 hours.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <Input placeholder="you@company.com" value={eEmail} onChange={(e) => setEEmail(e.target.value)}
                  className="bg-slate-950 border-slate-700" data-testid="email-input" />
                <Input placeholder="Your name (optional)" value={eName} onChange={(e) => setEName(e.target.value)}
                  className="bg-slate-950 border-slate-700" data-testid="name-input" />
                <Input placeholder="Company (optional)" value={eCompany} onChange={(e) => setECompany(e.target.value)}
                  className="bg-slate-950 border-slate-700" data-testid="company-input" />
              </div>
              <DialogFooter>
                <Button onClick={() => setEmailOpen(false)} variant="ghost" className="text-slate-400">Maybe later</Button>
                <Button onClick={submitEmail} disabled={submitting}
                  className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="submit-email-btn">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send my plan'}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <div className="py-6 text-center space-y-3">
              <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto" />
              <p className="text-lg font-bold text-slate-900">You're in.</p>
              <p className="text-sm text-slate-400">We'll send your plan within 24 hours.</p>
              <Button onClick={() => { setEmailOpen(false); setEmailDone(false); setEEmail(''); setEName(''); setECompany(''); }}
                variant="outline" className="border-slate-700 text-slate-600">Close</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <section className="px-4 pb-12">
        <div className="max-w-4xl mx-auto">
          <FAQSection
            title="Coach FAQ"
            items={faqsByPage.commitment_coach}
            testIdPrefix="coach-faq"
            variant="card"
          />
        </div>
      </section>
    </div>
  );
};

export default CommitmentCoach;
